import React, { useEffect, useState, forwardRef, useMemo, useCallback, useRef } from 'react';
import { find, isEmpty, isNull, split } from 'lodash';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { FieldTypes, parseSelectOptions } from 'utils/formUtils';
import { Form } from 'antd';

const Rapid7InsightMappingForm = forwardRef((props, ref) => {
  const [applications, setApplications] = useState(() => {
    if (props.record) {
      const { applicationId, applicationName } = props.record;
      return [{id: applicationId, name: applicationName}]
    }
    return [];
  });
  const [form] = Form.useForm();
  const editModeRef = useRef(!isEmpty(props.record));

  const [applicationId, setApplicationID] = useState(() => {
    if(props.record){
        const { applicationId } = props.record;
        return applicationId
    };
    return ""
  });

  const [scanConfigList, setScanConfigList] = useState(() => {
    if(props.record && !isNull(props.record.scanConfigId)){
        const { scanConfigId, scanConfigName,  applicationId} = props.record;
        return {
          [applicationId]: split(scanConfigId, ",").map((id, index) => ({
            id,
            name: split(scanConfigName, ",")[index],
          })),
        };
    }
    return {}
  });

  useEffect(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
    promise.then((response) => {
      const { projects } = response;
      setApplications(projects);
    }).catch(() => {
      message.error('Problem fetching projects');
    });

    return cancel;
  }, [props.loginConfigId,props.toolName]);

  const getExtraFieldsData = useCallback((config) => {
    const { applicationId, scanConfigId } = config;
    const selectedApplication = find(applications, {id: applicationId});
    const {name: applicationName} = selectedApplication;

    let payload = {
      applicationName,
      applicationId,
      scanConfigId: null,
      scanConfigName: null
    };

    if(!isEmpty(scanConfigId)){
        const data = scanConfigList[applicationId].reduce((acc,cv) =>{
          const { id, name } = cv;
          if(scanConfigId.includes(id)){
            acc.id.push(id);
            acc.name.push(name);
          }
          return acc;
        },{  id: [], name: [] });

        payload = {
          ...payload,
          scanConfigId: data.id.join(),
          scanConfigName: data.name.join(),
        }
    }
    return payload;
  },[applications, scanConfigList]);

  const fetchScanConfig = useCallback((appId) => {
    if (
      (editModeRef.current && appId) ||
      (appId && !scanConfigList[appId])
    ) {
        editModeRef.current = false;
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, type: "scanConfig", project: appId });
        promise.then((response) => {
          const { projects } = response;
          setScanConfigList((list) => ({
            ...list,
            [appId]: projects,
          }));
        }).catch(() => {
          message.error('Problem fetching scan configs');
        });
        return cancel
    };
  }, [props.toolName, props.loginConfigId, scanConfigList]);

  const onApplicationChange = useCallback(
    (value, field) => {
      setApplicationID(value);
      const { name } = field;
      const configurations = form.getFieldValue("configurations") || [];
      let updatedConfiguration = [...configurations];
      updatedConfiguration[name] = {
        ...updatedConfiguration[name],
        scanConfigId: undefined,
      };
      form.setFieldsValue({ configurations: updatedConfiguration });
    },
    [form]
  );

  useEffect(() => fetchScanConfig(applicationId), [applicationId, fetchScanConfig]);

  const extraFields = useMemo(() => ([
    {
      name: 'applicationId',
      colSpan: '12',
      type: FieldTypes.SELECT,
      label: 'Rapid7 Insight Project',
      options: applications.map(option => {
        const {name: label, id: value} = option;
        return { label, value }
      }),
      required: true,
      props: {
        autoFocus: true,
        onChange: onApplicationChange,
      }
    },
    {
      name: "scanConfigId",
      colSpan: 12,
      label: "Scan Config",
      placeholder: "Leave blank to send all configs",
      type: FieldTypes.MULTI_SELECT,
      props: {
        getFormListOptions: (fieldKey) => {
          const configs = form.getFieldValue("configurations")
          const appId= configs[fieldKey]?.applicationId
          return appId ? parseSelectOptions(scanConfigList[appId]) : []
        },
        setExtraProps: (field, listField) => {
          const allConfigs = form.getFieldsValue().configurations;
          const appId = allConfigs[listField.name]?.applicationId;
          return {
            disabled: !appId,
          };
        },
      },  
    }
  ]), [applications, onApplicationChange, form, scanConfigList]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { applicationId, scanConfigId } = config;
    setApplicationID(applicationId);
    return ({
        applicationId,
        scanConfigId: scanConfigId ? split(scanConfigId, ",") : undefined,
    });
  }, []);

  return (
    <GenericMappingForm
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      title='Map Rapid7 Insight Projects'
      deleteIconTooltip='Delete Configuraion'
      {...props}
      form={form}
    />
  )
})

export default Rapid7InsightMappingForm;